import { Component, EventEmitter, Input, Output, TemplateRef } from '@angular/core';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import {
    TreeViewDragEndEvent,
    TreeViewDropEvent,
    TreeViewFlatNode,
} from '@app/shared/models/tree-view.model';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { TreeViewDragStatus } from '@app/shared/constants/tree-view';

@Component({
    selector: 'app-tree-view',
    templateUrl: './tree-view.component.html',
    styleUrls: ['./tree-view.component.scss'],
    standalone: false,
})
export class TreeViewComponent<T> {
    @Input() set data(source: T[]) {
        this.dataSource.data = source || [];
        this.treeControl.expandAll();
    }
    @Input() headerTemplateRef: TemplateRef<unknown>;
    @Input() nodeTemplateRef: TemplateRef<unknown>;
    @Input() nodeDragTemplateRef?: TemplateRef<unknown>;
    @Input() disableDragAndDrop = true;
    @Input() isCompactMode = false;

    @Output() dragEnd: EventEmitter<TreeViewDragEndEvent<T>> = new EventEmitter<
        TreeViewDragEndEvent<T>
    >();

    treeControl = new FlatTreeControl<TreeViewFlatNode<T>>(
        (node) => node.level,
        (node) => node.expandable
    );

    /* eslint-disable @typescript-eslint/unbound-method, @typescript-eslint/no-unsafe-return */
    treeFlattener = new MatTreeFlattener(
        this.treeNodeTransformer,
        (node) => node.level,
        (node) => node.expandable,
        (node) => node['children']
    );
    /* eslint-enable @typescript-eslint/unbound-method, @typescript-eslint/no-unsafe-return */

    dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    draggingNode: TreeViewFlatNode<T>;
    treeViewDragStatus = TreeViewDragStatus.STAND_BY;

    hasChild = (_: number, node: TreeViewFlatNode<T>) => node.expandable;

    onDragStart(node: TreeViewFlatNode<T>) {
        this.treeControl.collapse(node);
        this.draggingNode = node;
        this.treeViewDragStatus = TreeViewDragStatus.DRAGGING;
    }

    onTreeViewDrop(event: TreeViewDropEvent<T>) {
        this.treeViewDragStatus = TreeViewDragStatus.STAND_BY;

        this.dragEnd.emit({
            ...event,
            source: this.draggingNode,
        });
    }

    onDropListDropped(event: CdkDragDrop<TreeViewFlatNode<T>>) {
        if (!event.isPointerOverContainer) {
            this.treeViewDragStatus = TreeViewDragStatus.STAND_BY;
        }
    }

    private treeNodeTransformer(node: T, level: number): TreeViewFlatNode<T> {
        /* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */
        const collection: unknown[] = node['children'];

        return {
            expandable: !!collection && collection.length > 0,
            level: level,
            item: node,
        };
    }
}
